import { cva } from 'class-variance-authority';

export const TEXT_BUTTON_SIZE = {
  XS: 'XS',
  S: 'S',
  M: 'M',
  L: 'L',
  XL: 'XL',
} as const;

export const TEXT_BUTTON_TYPE = {
  PRIMARY: 'primary',
  SECONDARY_GRAY: 'secondary-gray',
  SECONDARY: 'secondary',
  TERTIARY_GRAY: 'tertiary-gray',
  TERTIARY: 'tertiary',
  ON_BACKGROUND: 'on-background',
  TEXT: 'text',
  TEXT_GRAY: 'text-gray',
  DESTRUCTIVE: 'destructive',
  DESTRUCTIVE_TEXT: 'destructive-text',
} as const;

export const baseBtnStyle = [
  'flex',
  'items-center',
  'justify-center',
  'relative',
  'rounded-sm',
  'cursor-pointer',
  'font-semibold',
  'w-fit',
  'disabled:text-disabled',
  'disabled:pointer-events-none'
].join(' ');

export const iconStyle = ['flex', 'items-center', '[&>svg]:!text-current', '[&>svg]:stroke-2'].join(' ');

export const iconSizeStyle = {
  [TEXT_BUTTON_SIZE.XS]: ['w-3xs', 'h-3xs', '[&>svg]:!w-3xs', '[&>svg]:!h-3xs'].join(' '),
  [TEXT_BUTTON_SIZE.S]: ['w-l', 'h-l', '[&>svg]:!w-l', '[&>svg]:!h-l'].join(' '),
  [TEXT_BUTTON_SIZE.M]: ['w-2xl', 'h-2xl', '[&>svg]:!w-2xl', '[&>svg]:!h-2xl'].join(' '),
  [TEXT_BUTTON_SIZE.L]: ['w-2xl', 'h-2xl', '[&>svg]:!w-2xl', '[&>svg]:!h-2xl'].join(' '),
  [TEXT_BUTTON_SIZE.XL]: ['w-2xl', 'h-2xl', '[&>svg]:!w-2xl', '[&>svg]:!h-2xl'].join(' '),
};

export const colorStyle = {
  [TEXT_BUTTON_TYPE.PRIMARY]: [
    // basic
    'text-invert bg-brand-alt border border-cic-blue-500',

    // hover, active
    'hover:bg-button-primary-hover hover:border-cic-blue-600',
    'active:bg-button-primary-pressed active:border-cic-blue-700',

    // disabled
    'disabled:bg-disabled',
    'disabled:border disabled:border-disabled',
  ].join(' '),
  // 완료
  [TEXT_BUTTON_TYPE.DESTRUCTIVE]: [
    // basic
    'text-invert bg-destructive border border-error-500',

    // hover, active
    'hover:bg-button-destructive-hover hover:border-error-600',
    'active:bg-button-destructive-pressed hover:border-error-700',

    'disabled:bg-disabled',
    'disabled:border disabled:border-disabled',
  ].join(' '),
  [TEXT_BUTTON_TYPE.DESTRUCTIVE_TEXT]: [
    'text-negative',
    'hover:text-button-destructive-hover',
    'active:text-button-destructive-pressed',
  ].join(' '),
  [TEXT_BUTTON_TYPE.SECONDARY_GRAY]: [
    'text-primary bg-primary',
    'border border-primary',

    'hover:bg-hover hover:border-hover',
    'active:bg-pressed active:border-hover',

    'disabled:border-disabled',
  ].join(' '),
  [TEXT_BUTTON_TYPE.SECONDARY]: [
    'text-brand bg-primary',
    'border border-secondary-alt',

    'hover:bg-hover-alt hover:border-brand',
    'active:bg-pressed-invert active:border-brand',

    'disabled:border-disabled',
  ].join(' '),
  [TEXT_BUTTON_TYPE.TERTIARY_GRAY]: [
    'text-primary bg-none',

    'hover:bg-hover',
    'active:bg-pressed',
  ].join(' '),
  [TEXT_BUTTON_TYPE.TERTIARY]: [
    'text-brand bg-none',

    'hover:bg-hover-alt',
    'active:bg-pressed-invert',
  ].join(' '),
  [TEXT_BUTTON_TYPE.TEXT_GRAY]: [
    'text-primary',

    'hover:text-button-text-gray-hover',
    'active:text-button-text-gray-pressed',
  ].join(' '),
  [TEXT_BUTTON_TYPE.TEXT]: [
    'text-brand',

    'hover:text-button-text-hover',
    'active:text-button-text-pressed',
  ].join(' '),
  [TEXT_BUTTON_TYPE.ON_BACKGROUND]: [
    'text-primary bg-none',

    'hover:bg-button-on-bg-hover',
    'active:bg-button-on-bg-pressed',
  ].join(' '),
};

export const sizeStyle = {
  [TEXT_BUTTON_SIZE.XS]: [
    'h-[28px]',
    'py-3xs',
    'px-2xs',
    'gap-4xs',
    'font-semibold',
    'text-xs',
    'leading-2xs',
    'rounded-xs'
  ].join(' '),
  [TEXT_BUTTON_SIZE.S]: [
    'h-[36px]',
    'py-2xs',
    'px-xs',
    'gap-3xs',
    'font-semibold',
    'text-sm',
    'leading-sm'
  ].join(' '),
  [TEXT_BUTTON_SIZE.M]: [
    'h-[40px]',
    'py-xs',
    'px-m',
    'gap-3xs',
    'font-semibold',
    'text-sm',
    'leading-sm'
  ].join(' '),
  [TEXT_BUTTON_SIZE.L]: [
    'h-[44px]',
    'py-sm',
    'px-l',
    'gap-2xs',
    'font-semibold',
    'text-md',
    'leading-sm'
  ].join(' '),
  [TEXT_BUTTON_SIZE.XL]: [
    'h-[48px]',
    'py-m',
    'px-xl',
    'gap-2xs',
    'font-semibold',
    'text-md',
    'leading-sm'
  ].join(' '),
};

export const iconOnlySizeStyle = {
  [TEXT_BUTTON_SIZE.XS]: '!p-3xs [&_svg]:stroke-2',
  [TEXT_BUTTON_SIZE.S]: '!p-xs [&_svg]:stroke-2',
  [TEXT_BUTTON_SIZE.M]: '!p-xs [&_svg]:stroke-2',
  [TEXT_BUTTON_SIZE.L]: '!p-xs [&_svg]:stroke-2',
  [TEXT_BUTTON_SIZE.XL]: '!p-s [&_svg]:stroke-2',
};

export const iconOnlyChildSizeStyle = {
  [TEXT_BUTTON_SIZE.XS]: '[&_svg]:w-[12px] [&_svg]:h-[12px] [&_svg]:stroke-2',
  [TEXT_BUTTON_SIZE.S]: '[&_svg]:w-[16px] [&_svg]:h-[16px] [&_svg]:stroke-2',
  [TEXT_BUTTON_SIZE.M]: '[&_svg]:w-[20px] [&_svg]:h-[20px] [&_svg]:stroke-2',
  [TEXT_BUTTON_SIZE.L]: '[&_svg]:w-[24px] [&_svg]:h-[24px] [&_svg]:stroke-2',
  [TEXT_BUTTON_SIZE.XL]: '[&_svg]:w-[24px] [&_svg]:h-[24px] [&_svg]:stroke-2',
};

export const buttonVariants = cva(baseBtnStyle, {
  variants: {
    variant: {
      [TEXT_BUTTON_TYPE.PRIMARY]: colorStyle[TEXT_BUTTON_TYPE.PRIMARY],
      [TEXT_BUTTON_TYPE.SECONDARY_GRAY]:
        colorStyle[TEXT_BUTTON_TYPE.SECONDARY_GRAY],
      [TEXT_BUTTON_TYPE.SECONDARY]: colorStyle[TEXT_BUTTON_TYPE.SECONDARY],
      [TEXT_BUTTON_TYPE.TERTIARY_GRAY]:
        colorStyle[TEXT_BUTTON_TYPE.TERTIARY_GRAY],
      [TEXT_BUTTON_TYPE.TERTIARY]: colorStyle[TEXT_BUTTON_TYPE.TERTIARY],
      [TEXT_BUTTON_TYPE.TEXT_GRAY]: colorStyle[TEXT_BUTTON_TYPE.TEXT_GRAY],
      [TEXT_BUTTON_TYPE.TEXT]: colorStyle[TEXT_BUTTON_TYPE.TEXT],
      [TEXT_BUTTON_TYPE.DESTRUCTIVE]: colorStyle[TEXT_BUTTON_TYPE.DESTRUCTIVE],
      [TEXT_BUTTON_TYPE.ON_BACKGROUND]:
        colorStyle[TEXT_BUTTON_TYPE.ON_BACKGROUND],
      [TEXT_BUTTON_TYPE.DESTRUCTIVE_TEXT]:
        colorStyle[TEXT_BUTTON_TYPE.DESTRUCTIVE_TEXT],
    },
    size: {
      [TEXT_BUTTON_SIZE.XS]: sizeStyle[TEXT_BUTTON_SIZE.XS],
      [TEXT_BUTTON_SIZE.S]: sizeStyle[TEXT_BUTTON_SIZE.S],
      [TEXT_BUTTON_SIZE.M]: sizeStyle[TEXT_BUTTON_SIZE.M],
      [TEXT_BUTTON_SIZE.L]: sizeStyle[TEXT_BUTTON_SIZE.L],
      [TEXT_BUTTON_SIZE.XL]: sizeStyle[TEXT_BUTTON_SIZE.XL],
    }
  },
  compoundVariants: [
    { variant: TEXT_BUTTON_TYPE.TEXT, className: '!p-0 !h-auto' },
    { variant: TEXT_BUTTON_TYPE.TEXT_GRAY, className: '!p-0 !h-auto' },
    { variant: TEXT_BUTTON_TYPE.DESTRUCTIVE_TEXT, className: '!p-0 !h-auto' },
  ],
  defaultVariants: {
    variant: TEXT_BUTTON_TYPE.PRIMARY,
    size: TEXT_BUTTON_SIZE.S
  }
});
